import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import Validate from 'app/validation/validation';
import { executeWithCaptcha, getMediaPath } from 'app/functions';
import AnalyticsManager from 'app/analytics';
import { colors, spacing } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import { TextInputHot, MODE } from 'components/common/TextInputHot';
import { ButtonHot } from 'components/common/ButtonHot';
import Recaptcha from 'components/services/google/recaptcha';
import ImageHeader from 'components/image-header';
import {
  useGetMembershipGeneratorPageQuery,
  useGetClubMemberCertificateMutation,
} from 'redux/api/relations.api';
import { generateTestId } from 'app/functions';
import * as Actions from 'redux/actions';
import * as Popups from 'constants/popup-types';

export const MembershipCertificateSection = ({ testID }) => {
  const dispatch = useDispatch();
  const userData = useSelector((store) => store.config.UserData);
  const text = useSelector((store) => store.config.Translations);

  const [firstTry, setFirstTry] = useState(true);
  const [form, setForm] = useState({
    id: { val: '', valid: false, rules: ['not_empty', 'id'], errMsg: '' },
    email: { val: '', valid: false, rules: ['should_add_email_if_not_exists'], errMsg: '' },
    full_name: { val: '', valid: false, rules: ['not_empty', 'full_name'], errMsg: '' },
  });

  const { data: membershipPageData } = useGetMembershipGeneratorPageQuery();
  const [membershipCheckPage, setMembershipCheckPage] = useState(membershipPageData);

  const [responseMessage, setResponseMessage] = useState(null);
  const [getClubMemberCertificate, { isLoading: isLoadingClubMemberCertificate }] =
    useGetClubMemberCertificateMutation();

  const [formStatus, setFormStatus] = useState(false);

  useEffect(() => {
    if (membershipPageData) {
      setMembershipCheckPage(membershipPageData);
    }
  }, [membershipPageData]);

  const userId = userData?.user_id;
  const userFullName = userData ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() : '';
  const userEmail = userData?.email || '';

  useEffect(() => {
    if (!userData) return;

    setForm((prev) => {
      const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
      const id = `${userData.user_id || ''}`;
      const email = userData.email || '';
      if (prev.full_name.val === fullName && prev.id.val === id && prev.email.val === email) {
        return prev;
      }
      return {
        ...prev,
        full_name: {
          ...prev.full_name,
          val: fullName,
          valid: Validate(fullName, prev.full_name.rules).valid,
          errMsg: Validate(fullName, prev.full_name.rules).msg,
        },
        id: {
          ...prev.id,
          val: id,
          valid: Validate(id, prev.id.rules).valid,
          errMsg: Validate(id, prev.id.rules).msg,
        },
        email: {
          ...prev.email,
          val: email,
          valid: Validate(email, prev.email.rules).valid,
          errMsg: Validate(email, prev.email.rules).msg,
        },
      };
    });
  }, [userId, userFullName, userEmail]);

  useEffect(() => {
    if (formStatus) {
      AnalyticsManager.logRequestMembershipCard();
    }
  }, [formStatus]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    const validate = Validate(value, form[name].rules);
    setForm((prevForm) => ({
      ...prevForm,
      [name]: { ...prevForm[name], val: value, valid: validate.valid, errMsg: validate.msg },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userData) {
      dispatch(
        Actions.addPopup({
          type: Popups.LOGIN,
          text: text?.['login_to_check_membership'],
        })
      );
      return;
    }

    let form_valid = true;
    const new_state = { ...form };

    for (const field in form) {
      const validate = Validate(form[field].val, form[field].rules);
      new_state[field].valid = validate.valid;
      new_state[field].errMsg = validate.msg;
      if (!validate.valid) form_valid = false;
    }
    setForm(new_state);
    setFirstTry(false);

    if (form_valid) {
      executeWithCaptcha((token) => {
        const payload = {
          userId: userData.user_id,
          code: userData?.cardsArr?.length > 0 ? userData.cardsArr[0]?.last_digits : '',
          userName: `${userData?.first_name} ${userData?.last_name}`,
          email: userData.email,
          'g-recaptcha-response': token,
        };
        handleClubMemberCertificate(payload);
      });
    }
  };

  const handleClubMemberCertificate = async (payload) => {
    try {
      const response = await getClubMemberCertificate(payload);
      if (response?.data?.validBool) {
        setFormStatus(true);
        setResponseMessage(membershipCheckPage?.successMessage);
      } else if (response?.data && !response.data.validBool) {
        setFormStatus(false);
        setResponseMessage(response.data.returnValue);
      }
    } catch (error) {
      setResponseMessage(error?.data?.returnValue || error?.data?.content || error?.message || 'אירעה שגיאה');
      console.error('Error fetching club member certificate:', error);
    }
  };

  const showNameErrorMsg = !firstTry && !form.full_name.valid;
  const showIdErrorMsg = !firstTry && !form.id.valid;
  const showEmailErrorMsg = !firstTry && !form.email.valid;

  if (!membershipCheckPage) {
    return null;
  }

  const imagePath = membershipCheckPage?.imagePath;
  const mobileImagePath = membershipCheckPage?.mobileImagePath;
  const hasBannerContent = imagePath || mobileImagePath || membershipCheckPage?.bannerHeader || membershipCheckPage?.bannerSubHeader;

  const bannerWrapperSx = {
    width: '100%',
    '& .image-header-banner': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: 1,
      '& .title-wrapper': {
        textAlign: 'center',
        margin: '0 auto',
      },
      '& .bg-img': {
        objectFit: 'cover',
      },
    },
  };

  return (
    <Box
      testId={testID}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: `${spacing.xl}px`,
      }}
    >
      <Box sx={{p: `${spacing.md}px` }}>
      <TextHot size="xxxl" bold="bold" color="primary" isLtr={true}>
        {membershipCheckPage.bannerHeader}
      </TextHot>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${spacing.sm}px`,
          mb: 2,
          textAlign: 'center',
          alignItems: 'center',
        }}
      >
        <TextHot size="xxxl" bold="bold" color="primary" isLtr={true}>
          {membershipCheckPage.header}
        </TextHot>
        {!formStatus && (
          <TextHot size="m" color="secondary" sx={{ maxWidth: 720 }} isLtr={true}>
            {userData ? membershipCheckPage.subHeader : (text?.['login_to_check_membership'] || membershipCheckPage.subHeader)}
          </TextHot>
        )}
      </Box>

      {!formStatus && !responseMessage && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: `${spacing.lg}px`, md: `${spacing.xxl}px` },
            mx: 'auto',
            width: '100%',
            maxWidth: { xs: 480, md: 560 },
            '& .MuiInputBase-root': {
              height: { xs: 48, md: 56 },
              fontSize: { xs: 16, md: 18 },
              padding: { xs: `${spacing.md}px`, md: `${spacing.lg}px` },
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: 16, md: 18 },
            },
            '& .MuiTypography-root': {
              fontSize: { xs: 16, md: 18 },
            },
          }}
        >
          {userData ? (
            <>
              <TextInputHot
                type="text"
                name="full_name"
                label={membershipCheckPage.textFieldName}
                placeholder={membershipCheckPage.textFieldName}
                value={form.full_name.val}
                onChange={handleInput}
                error={showNameErrorMsg}
                helperText={showNameErrorMsg ? form.full_name.errMsg : ''}
                mode={MODE.FORM}
                testID={generateTestId(testID, 'full-name-input')}
              />

              <TextInputHot
                type="text"
                name="email"
                label={membershipCheckPage.textFieldEmail}
                placeholder={membershipCheckPage.textFieldEmail}
                value={form.email.val}
                onChange={handleInput}
                disabled
                error={showEmailErrorMsg}
                helperText={showEmailErrorMsg ? form.email.errMsg : ''}
                mode={MODE.FORM}
                testID={generateTestId(testID, 'email-input')}
              />

              <Box
                sx={{
                  visibility: 'hidden',
                  height: 0,
                  overflow: 'hidden',
                  margin: 0,
                  mt: 0,
                  mb: -4,
                }}
              >
                <Recaptcha isFunction={false} enterprise />
              </Box>
            </>
          ) : null}

          <ButtonHot
            type="submit"
            mode="contained"
            variant="primary"
            size="l"
            fullWidth
            disabled={isLoadingClubMemberCertificate}
            testID={generateTestId(testID, 'submit-button')}
            sx={{
              mt: 2,
              height: { xs: 48, md: 56 },
              fontSize: { xs: 16, md: 18 },
            }}
          >
            {membershipCheckPage.btnText}
          </ButtonHot>
        </Box>
      )}

      {formStatus && responseMessage && (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: colors.palette.green100,
            border: `1px solid ${colors.palette.green500}`,
            textAlign: 'center',
          }}
        >
          <TextHot size="l" color="primary">
            {responseMessage}
          </TextHot>
        </Box>
      )}

      {!formStatus && responseMessage && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: colors.palette.red100,
            border: `1px solid ${colors.palette.red500}`,
            textAlign: 'center',
          }}
        >
          <TextHot size="m" color="error">
            {responseMessage}
          </TextHot>
        </Box>
      )}
    </Box>
  );
};

export default MembershipCertificateSection;
